import { FlexLayout, Typography } from '@eduact/ed-system'
import Spacer from '@eduact/ed-components/Spacer'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import stampAutoDir from '@Utilities/StampAutoDir'
import { QuestionContentWrapper } from './QuestionStyled'
import { OptionEntry, entryMatchesOption, optionEntries } from './matching'

type MrqAnswerValue = { answer: string[]; option_ids?: Array<number | null> } | null | undefined

type Props = {
	question: any
	onChange: (answer: any) => void
}

/**
 * Vendored from ed-components 0.4.10 Test/MrqQuestion. Same rendering; the
 * checkbox identity moved from the option text to the option id (text
 * fallback for legacy payloads) — see VENDORED.md.
 */
const MrqQuestion: React.FC<Props> = ({ question, onChange }) => {
	const { t } = useTranslation(['test'])
	const entries = optionEntries(question)
	const content = useMemo(() => stampAutoDir(question.content), [question.content])
	const current = question.answer as MrqAnswerValue

	// A stored entry as {answer, option_id} pairs; legacy answers have no
	// option_ids array, so ids default to null and matching falls to text.
	const selectedEntries = (current?.answer ?? []).map((answer, index) => ({
		answer,
		option_id: current?.option_ids?.[index] ?? null,
	}))

	const isSelected = (entry: OptionEntry) => selectedEntries.some(stored => entryMatchesOption(stored, entry))
	// Stored selections whose option no longer renders (edited or deleted
	// since they were saved): they can't pre-check anything, so surface them
	// instead of letting the selection silently look smaller than it was.
	const orphanedSelections =
		entries.length > 0 ? selectedEntries.filter(stored => !entries.some(entry => entryMatchesOption(stored, entry))) : []

	/**
	 * Rebuild the whole selection from the rendered options on every toggle.
	 * A stale stored text that matches no rendered option simply drops out —
	 * the same outcome the text-keyed version produced, minus its ambiguity.
	 */
	const toggle = (entry: OptionEntry) => {
		const next = entries.filter(option => (option === entry ? !isSelected(option) : isSelected(option)))
		const value: { answer: string[]; option_ids?: number[] } = { answer: next.map(option => option.choice) }
		if (next.length > 0 && next.every(option => option.id != null)) {
			value.option_ids = next.map(option => option.id as number)
		}
		onChange(value)
	}

	return (
		<Spacer p={{ sm: '1rem' }}>
			<QuestionContentWrapper dangerouslySetInnerHTML={{ __html: content }} />
			<Spacer mb={{ sm: '0.813rem', lg: '2.313rem' }} />
			<FlexLayout px={{ sm: '0.813rem' }} flexDirection="column">
				<AnswersLabel fontSize={{ sm: '0.5rem', lg: '1rem' }} />
				<Spacer mb={{ sm: '6px', lg: '1rem' }} />
				<FlexLayout flexDirection={'column'}>
					{entries.map((entry, index) => (
						<FlexLayout alignItems={'center'} key={`${entry.id ?? entry.choice}-${index}`} mb={{ sm: '0.75rem' }}>
							<StyledCheckbox
								onChange={() => toggle(entry)}
								type={'checkbox'}
								id={`${entry.id ?? entry.choice}`}
								checked={isSelected(entry)}
								value={entry.choice}
								name={`answer-${question.id}`}
							/>
							<Spacer mx={{ sm: '4px' }} />
							<Typography cursor={'pointer'} onClick={() => toggle(entry)} fontSize={{ sm: '0.75rem', lg: '1.125rem' }}>
								<span dir="auto">{entry.choice}</span>
							</Typography>
						</FlexLayout>
					))}
					{orphanedSelections.map((stored, index) => (
						<Typography key={`orphan-${stored.option_id ?? stored.answer}-${index}`} fontSize={{ sm: '0.75rem', lg: '1.125rem' }}>
							<span dir="auto">
								{stored.answer} — {t('option_no_longer_exists')}
							</span>
						</Typography>
					))}
				</FlexLayout>
			</FlexLayout>
		</Spacer>
	)
}

export default MrqQuestion

const StyledCheckbox = styled.input`
	width: 1rem;
	height: 1rem;
	${({ theme }) => `${theme.mediaQueries.large}{
		width :1.25rem;
		height :1.25rem;
	}`}
`
const AnswersLabel = styled(Typography)`
	color: ${props => props.theme.colors.silver};
`
